import _get from 'lodash/get';
import { BaseParserOptions, BaseParserConstructor } from './SharedTypes';

export type APIResource = {
  resource: fhir.Resource;
};

/**
 * Given a target fhir property path and corresponding fhir type parser, will process a target property from a raw fhir resource
 *
 * @export
 * @class FhirPropertyParser
 */
class FhirPropertyParser {
  private fhirPath: string;
  private FhirTypeParser: BaseParserConstructor;
  private typeParserOptions: BaseParserOptions;

  /**
   * Creates an instance of FhirPropertyParser.
   * @param {string} fhirPath - the path of the target property of the fhir resource
   * @param {BaseParserConstructor} FhirTypeParser - the corresponding type parser for the target property
   * @param {BaseParserOptions} typeParserOptions - options to relay to the type parser
   * @memberof FhirPropertyParser
   */
  constructor(
    fhirPath: string,
    FhirTypeParser: BaseParserConstructor,
    typeParserOptions?: BaseParserOptions,
  ) {
    if (!fhirPath) throw new Error('Fhir path required to parse table value');

    this.fhirPath = fhirPath;
    this.FhirTypeParser = FhirTypeParser;
    this.typeParserOptions = typeParserOptions || {};
  }

  /**
   * Determines if the target property exists on the resource
   *
   * @param {APIResource} apiResource - fhirResource to search for target property
   * @returns
   * @memberof FhirPropertyParser
   */
  propertyExists(apiResource: APIResource): boolean {
    return _get(apiResource, this.fhirPath) !== undefined;
  }

  /**
   * Extracts the type-dependent display value from the fhir property
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getDisplayValue(apiResource: APIResource): string | number {
    const fhirValue = _get(apiResource, this.fhirPath);
    if (!this.FhirTypeParser) {
      return fhirValue;
    }

    const parser = new this.FhirTypeParser(this.typeParserOptions);
    return parser.getDisplayValue(fhirValue);
  }

  /**
   * Extracts the type-dependent sort value from the fhir property
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getSortValue(apiResource: APIResource): string | number {
    const fhirValue = _get(apiResource, this.fhirPath);
    if (!this.FhirTypeParser) {
      return fhirValue;
    }

    const parser = new this.FhirTypeParser(this.typeParserOptions);
    return parser.getSortValue(fhirValue);
  }

  /**
   * Extracts the the raw value from the fhir property
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getRawValue(apiResource: APIResource): any {
    return _get(apiResource, this.fhirPath, null);
  }
}

export type IDToParserMap = {
  [id: string]: FhirPropertyParser;
};

/**
 * Given a collection of FhirPropertyParsers, will iteratively attempt to extract data from a raw fhir resource
 *
 * @export
 * @class FhirPropertyParserGroup
 */
class FhirPropertyParserGroup {
  private fhirPropertyParsers: FhirPropertyParser[];
  private idToTargetParserMap: IDToParserMap;
  private enableTargetParserCaching: boolean;

  /**
   * Creates an instance of FhirPropertyParserGroup.
   * @param {FhirPropertyParser[]} fhirPropertyParsers - List of Fhir Property Parsers to try (in order) to parse a fhir resource
   * @param {boolean} [enableTargetParserCaching=false] - Enables caching of target parser based on fhir resource id.  This can improve performance if parsing static fhir resource
   * @memberof FhirPropertyParserGroup
   */
  constructor(
    fhirPropertyParsers: FhirPropertyParser[],
    enableTargetParserCaching = false,
  ) {
    this.fhirPropertyParsers = fhirPropertyParsers || [];

    this.idToTargetParserMap = {};
    this.enableTargetParserCaching = enableTargetParserCaching;
  }

  /**
   * Retrieve the target parser (possibly from cache) for the given fhir resource
   *
   * @param {APIResource} apiResource - raw fhir resource
   * @memberof FhirPropertyParserGroup
   */
  _getTargerParserForResource(apiResource: APIResource): FhirPropertyParser {
    if (this.enableTargetParserCaching) {
      const resource = _get<APIResource, 'resource', fhir.Resource>(
        apiResource,
        'resource',
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        null,
      );
      const fhirResourceId = _get<fhir.Resource, 'id', string>(
        resource,
        'id',
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        null,
      );

      if (fhirResourceId && this.idToTargetParserMap[fhirResourceId]) {
        // cached target parser found
        return this.idToTargetParserMap[fhirResourceId];
      }

      const targetParser = this.fhirPropertyParsers.find(parser =>
        parser.propertyExists(apiResource),
      );

      // add parser to cache if applicable
      if (targetParser && fhirResourceId) {
        this.idToTargetParserMap[fhirResourceId] = targetParser;
      }

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return targetParser;
    } else {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return this.fhirPropertyParsers.find(parser =>
        parser.propertyExists(apiResource),
      );
    }
  }

  /**
   * Extracts the type-dependent display value from the fhir resource
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getDisplayValue(apiResource: APIResource): string | number {
    const targetParser = this._getTargerParserForResource(apiResource);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return targetParser ? targetParser.getDisplayValue(apiResource) : null;
  }

  /**
   * Extracts the type-dependent sort value from the fhir resource
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getSortValue(apiResource: APIResource): any {
    const targetParser = this._getTargerParserForResource(apiResource);
    return targetParser ? targetParser.getSortValue(apiResource) : null;
  }

  /**
   * Extracts the the raw value from the fhir resource
   *
   * @param {APIResource} apiResource
   * @returns
   * @memberof FhirPropertyParser
   */
  getRawValue(apiResource: APIResource): any {
    const targetParser = this._getTargerParserForResource(apiResource);
    return targetParser ? targetParser.getRawValue(apiResource) : null;
  }
}

export default FhirPropertyParser;
export { FhirPropertyParserGroup };
